// MultiImgRow — ряд из нескольких image-thumbs + кнопка «+» для добавления.
// Используется для моделей с supportsMultipleImages (reference-набор).
//
// Извлечён из GenerateInputBar.tsx чтобы переиспользовать в UploadsRow
// (которая рендерится на /generate, home и /bots/*-виджете).
import { Fragment } from 'react'
import type { UploadedFile } from '../utils/uploadHelpers'
import Tooltip from '../../../components/Tooltip'
import SideTooltip from '../../../components/SideTooltip'
import { Icon } from '../../../../public/icons/Icon.tsx'

export interface MultiImgRowProps {
	images: UploadedFile[]
	maxImages: number
	isLoading: boolean
	isUploading: boolean
	inputRef: React.RefObject<HTMLInputElement | null>
	onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRemove: (idx: number) => void
	tooltip?: string
	onHintDismiss?: () => void
	onShowPicker?: (buttonEl: HTMLElement) => void
	/** Скелет-плитки пустых слотов до лимита (tool-режим: юзер видит,
	 *  сколько файлов можно добавить). В классическом баре не рендерятся. */
	slotSkeletons?: boolean
	/** Метка «фото персонажа» (кольцо + мини-аватар) для тумб, чей url в
	 *  markedUrls — сверка по path (подпись presigned меняется, путь стабилен). */
	characterMark?: { avatar: string | null; letter: string }
	markedUrls?: string[]
}

export default function MultiImgRow({
	images,
	maxImages,
	isLoading,
	inputRef,
	onUpload,
	onRemove,
	tooltip,
	onHintDismiss,
	onShowPicker,
	slotSkeletons,
	characterMark,
	markedUrls,
}: MultiImgRowProps) {
	const addBtn = (
		<button
			type="button"
			className="gm-upload-btn gm-upload-btn--sm"
			onClick={(e) => {
				onHintDismiss?.()
				if (onShowPicker) onShowPicker(e.currentTarget)
				else inputRef.current?.click()
			}}
			disabled={isLoading}
			aria-label="Ваше фото"
		>
			<Icon id="gen-add" size={20} />
		</button>
	)
	return (
		<div className="gm-multi-row">
			{/* Обёртка тумб: в классическом баре display:contents (невидима),
			    в tool-режиме — зона контента со своим скроллом (см. _tool-mode). */}
			<div className="gm-multi-row__items">
			{images.map((img, idx) => {
				const path = (u: string) => u.split('?', 1)[0]
				const marked =
					!!characterMark && !!img.url && !!markedUrls?.some((u) => path(u) === path(img.url!))
				const card = (
					<div className={`gm-upload-thumb gm-upload-thumb--sm${marked ? ' gm-upload-thumb--char' : ''}`}>
						{img.preview && (
							<img src={img.preview} alt={`ref ${idx + 1}`} className="gm-upload-thumb__media" />
						)}
						{!img.url && (
							<div className="gm-upload-thumb__overlay">
								<div className="gw__loading-dots">
									<span />
									<span />
									<span />
								</div>
							</div>
						)}
						{img.url && (
							<div className="gm-upload-thumb__ok">
								<Icon id="gen-check" size={10} />
							</div>
						)}
						{/* Фото подогнали под нейросеть (utils/imagePrepare): что именно — в подсказке; на телефоне
						    это уже сказало уведомление при загрузке. Низ-лево — место бейджей тумбы. */}
						{img.note && (
							<span className="gm-upload-thumb__fixed" role="img" title={img.note} aria-label={img.note}>
								<Icon id="gen-aspect-ratio" size={9} />
							</span>
						)}
						{/* Чип-аватар убран (владелец: лишний дубль — капсула в шапке
						    уже показывает «кто»); метка — рамка --char на тумбе. */}
						<button
							className="gm-upload-thumb__remove"
							onClick={() => onRemove(idx)}
							aria-label="Удалить"
						>
							<Icon id="gen-close" size={14} />
						</button>
					</div>
				)
				// Placeholder из gallery-пикера может прийти без preview (старые
				// файлы без webp) — Tooltip с пустым imagePreview не оборачиваем.
				if (!img.preview) return <Fragment key={`pending-${idx}`}>{card}</Fragment>
				return (
					<Tooltip key={img.preview} imagePreview={img.preview}>
						{card}
					</Tooltip>
				)
			})}
			{slotSkeletons &&
				Array.from({ length: Math.max(0, maxImages - images.length) }).map((_, i) => (
					<span key={`skel-${i}`} className="gm-upload-skel" aria-hidden="true" />
				))}
			</div>
			{images.length < maxImages &&
				(tooltip ? <SideTooltip text={tooltip}>{addBtn}</SideTooltip> : addBtn)}
			<input
				ref={inputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
				multiple
				onChange={onUpload}
				// isUploading НЕ блокирует: multi-ряд принимает файлы параллельно
				// (у каждого тамба своя индикация), иначе «Загрузить фото» во время
				// загрузки из галереи молча не открывал системный диалог.
				disabled={isLoading}
				style={{ display: 'none' }}
			/>
		</div>
	)
}
